import { find } from '@execonline-inc/collections';
import { toResult } from '@execonline-inc/maybe-adapter';
import {
  Link as LinkR,
  Linkable as LinkableR,
  PossiblyLinkable as PossiblyLinkableR,
  Resource as ResourceR,
  ResourceWithErrors as ResourceWithErrorsR,
  ResourceWithMetadata as ResourceWithMetadataR,
  payload as payloadR,
  resource as resourceR,
} from '@execonline-inc/resource';
import { Result } from 'resulty';

const rels = [
  'achievements',
  'adaptive',
  'add',
  'add-complete',
  'add-current',
  'add-progress-detail',
  'announcements',
  'app-session',
  'assertion-backend',
  'assignments',
  'attachment',
  'attributes',
  'auth',
  'avatar',
  'availabilities',
  'book',
  'broadcast-message',
  'broadcast-poll',
  'broadcast-poll-close',
  'calendar',
  'calendaring-app',
  'cancel',
  'children',
  'client-portal-app',
  'coach',
  'coaching-portal-app',
  'coaching-scheduler',
  'partner-portal-app',
  'coach-communities',
  'coach-team-options',
  'coach-teams-summary',
  'collection',
  'conference-room',
  'country-options',
  'country-region-options',
  'communities',
  'compressed-enclosure',
  'course-document-template',
  'create',
  'create-engagement',
  'current-events',
  'current-user',
  'dashboard',
  'delete',
  'direct-enrollment-discovery-portal',
  'direct-upload',
  'discovery-portal',
  'discussions',
  'download',
  'download-digital-cert-preview',
  'download-overview-pdf',
  'edit',
  'edit-profile',
  'enclosure',
  'enroll',
  'enterprise-portal-app',
  'error',
  'events',
  'experiences',
  'experience-filters',
  'experience-prices',
  'experience-template-link',
  'experiences-dictionary',
  'external-conference-url',
  'first',
  'freebusy',
  'freebusy-coaching',
  'freebusy-program',
  'history',
  'icon',
  'join-event',
  'in-reply-to',
  'last',
  'last-read-message',
  'launch-poll',
  'launch-poll-close',
  'learning-partners',
  'learning-partner-resources',
  'live-meeting-recording-resources',
  'linkables',
  'login',
  'logo',
  'logo-on-dark',
  'logo-on-white',
  'logout',
  'logs',
  'messages',
  'meta',
  'my-hand-broadcast',
  'my-hand-down',
  'my-hand-up',
  'my-personalized-resources',
  'my-uploaded-resources',
  'needed-for-assignment-resources',
  'next',
  'notes',
  'notes-create',
  'notifications',
  'orientation-commencement',
  'original',
  'organization',
  'other-hand-down',
  'overview-video',
  'page-template',
  'parent',
  'preferred-language',
  'present-participants',
  'prev',
  'preview',
  'previous',
  'primary-competencies',
  'print',
  'privacy',
  'product-collections',
  'product-details',
  'professors',
  'profile',
  'program',
  'program-template',
  'programs',
  'progress',
  'promotables',
  'promotable-groups',
  'promotable-marketing-image',
  'registration-invitation-template',
  'remove',
  'remove-complete',
  'resume',
  'request-coach',
  'reschedule',
  'reset-password',
  'resized-avatar',
  'resources-dashboard',
  'replies',
  'resource-template',
  'resources',
  'results-create',
  'roster',
  'school-partner',
  'school-partners',
  'scorm-tracking',
  'segment-preview-template',
  'shared-invitation-template',
  'staff-join',
  'staff-left',
  'self',
  'simple-file-upload',
  'sso',
  'success',
  'supported-languages',
  'sprite',
  'subscribe',
  'summary',
  'support',
  'survey',
  'targeted-invitation-template',
  'team',
  'team-details',
  'team-template',
  'teams',
  'teams-summary',
  'terms',
  'terms_and_conditions',
  'thumb',
  'thumbnail',
  'timer',
  'token-renewal',
  'toolings',
  'unified-login',
  'unsubscribe',
  'unsubscribe-invitation-template',
  'unsubscribe-progress-email-template',
  'update',
  'update-password',
  'use-case',
  'user',
  'video',
  'view',
  'viewable-attachment-template',
  'validate-token',
  'last-read-message',
  'commerce',
  'pending-surveys',
  'auto-launchables',
  'participant-postponables',
  'search-discovery-portal',
  'search-experiences',
  'learning-collection-logo',
  'dropping-program',
  // DVLP-7860: each license offer links to POST /aep/:uuid/license_purchases.
  // Without it here, linkDecoder rejects the rel and the whole offers payload fails.
  'purchase',
] as const;

export type Rel = (typeof rels)[number];

export const toRel = (value: string): Result<string, Rel> =>
  toResult(
    `Expected to find an HTTP rel string. Instead I found ${value}`,
    find((rel) => rel === value, rels),
  );

export type Link = LinkR<Rel>;
export type Linkable = LinkableR<Rel>;
export type PossiblyLinkable = PossiblyLinkableR<Rel>;

export type Resource<T> = ResourceR<T, Rel>;
export type ResourceWithErrors<T> = ResourceWithErrorsR<T, Rel>;

export type ResourceWithMetadata<T, M> = ResourceWithMetadataR<T, M, Rel>;

export interface ResourceWithEmbeddedR<T, E, Rel extends string> extends ResourceR<T, Rel> {
  embedded: E;
}

export type ResourceWithEmbedded<T, E> = ResourceWithEmbeddedR<T, E, Rel>;

export const resourceWithEmbeddedR: <Rel extends string, T, E>(
  links: readonly LinkR<Rel>[],
  payload: T,
  embedded: E,
) => ResourceWithEmbeddedR<T, E, Rel> = (links, payload, embedded) => ({
  links,
  payload,
  embedded,
});

export const resourceWithEmbedded: <T, E>(
  links: ReadonlyArray<Link>,
  payload: T,
  embedded: E,
) => ResourceWithEmbedded<T, E> = resourceWithEmbeddedR;

export const resource: <T>(links: ReadonlyArray<Link>, payload: T) => Resource<T> = resourceR;

export const payload: <A, R extends Resource<A>>(r: R) => A = payloadR;

export function links<T>(resource: Resource<T>): ReadonlyArray<Link> {
  return resource.links;
}

export type { IdentifiablePayload, ServerError } from '@execonline-inc/resource';
